import {
  alertRuleListSchema,
  alertRuleSchema,
  createDefaultAlertRulesResponseSchema,
  type CreateAlertRuleRequest,
  type CreateDefaultAlertRulesRequest,
  type CreateDefaultAlertRulesResponse,
  assetListSchema,
  assetSchema,
  auditLogListSchema,
  dashboardSummarySchema,
  deviceInstallationListSchema,
  deviceInstallationSchema,
  controlDeviceChannelResponseSchema,
  deviceChannelSchema,
  deviceListSchema,
  deviceSchema,
  gatewayListSchema,
  gatewayOutageListSchema,
  gatewayOutageSchema,
  gatewaySchema,
  incidentListSchema,
  incidentSchema,
  messagingContactListSchema,
  messagingContactSchema,
  organizationListSchema,
  organizationSchema,
  platformSettingsSchema,
  type PlatformSettings,
  type UpdatePlatformSettingsRequest,
  reconciliationReportSchema,
  type ReconciliationReport,
  siteListSchema,
  siteSchema,
  telemetryListSchema,
  telemetrySeriesSchema,
  temperatureRecordSchema,
  userListSchema,
  userSchema,
  webhookEventListSchema,
  simulateUplinkResponseSchema,
  type AcknowledgeIncidentRequest,
  type AlertMetric,
  type AlertRule,
  type AlertRuleList,
  type Asset,
  type AssetList,
  type AuditLogList,
  type CreateAssetRequest,
  type ControlDeviceChannelRequest,
  type ControlDeviceChannelResponse,
  type CreateDeviceRequest,
  type CreateGatewayRequest,
  type CreateMessagingContactRequest,
  type CreateOrganizationRequest,
  type CreateSiteRequest,
  type UpdateAssetRequest,
  type UpdateSiteRequest,
  type CreateUserRequest,
  type DashboardSummary,
  type Device,
  type DeviceChannel,
  type DeviceInstallation,
  type DeviceInstallationList,
  type DeviceList,
  type EnabledHardwareMap,
  type FailInstallationRequest,
  type FeaturesResponse,
  type AcknowledgeGatewayOutageRequest,
  type Gateway,
  type GatewayList,
  type GatewayOutage,
  type GatewayOutageList,
  type LinkGatewaysRequest,
  type Incident,
  type IncidentList,
  type MessagingContact,
  type MessagingContactList,
  type OpenInstallationRequest,
  type Organization,
  type OrganizationList,
  type ReinstateConsentRequest,
  type RevokeConsentRequest,
  type Site,
  type SiteList,
  loanAgreementListSchema,
  loanAgreementSchema,
  serviceAgreementListSchema,
  serviceAgreementSchema,
  type AttestSignatureRequest,
  type AttestServiceSignatureRequest,
  type CreateLoanAgreementRequest,
  type CreateServiceAgreementRequest,
  type LoanAgreement,
  type LoanAgreementList,
  type LoanAgreementStatus,
  type RecordReturnRequest,
  type ServiceAgreement,
  type ServiceAgreementList,
  type ServiceAgreementStatus,
  type UpdateLoanAgreementRequest,
  type UpdateServiceAgreementRequest,
  type SuspendOrganizationRequest,
  type SimulateUplinkRequest,
  type SimulateUplinkResponse,
  type TelemetryBucket,
  type TelemetryList,
  type TelemetryReading,
  type TelemetrySeries,
  type TemperatureRecord,
  type UpdateAlertRuleRequest,
  type UpdateDeviceChannelRequest,
  type UpdateDeviceRequest,
  type UpdateGatewayRequest,
  type UpdateUserRequest,
  type User,
  type UserList,
  type WebhookEventList,
  type WebhookEventStatus,
  testIncidentEmailResponseSchema,
  testIncidentWhatsAppResponseSchema,
  type TestIncidentEmailRequest,
  type TestIncidentEmailResponse,
  type TestIncidentWhatsAppRequest,
  type TestIncidentWhatsAppResponse,
} from "@/contracts";
import { queryOptions, type UseMutationOptions } from "@tanstack/react-query";

import type { ApiClient } from "@/lib/api-client";

/**
 * Query keys and fetchers in one file.
 *
 * Every key starts with its resource so a mutation can invalidate a whole
 * family without listing each filter combination it might have produced.
 */
export const queryKeys = {
  features: ["features"] as const,
  dashboard: ["dashboard", "summary"] as const,
  devices: (filters: DeviceFilters) => ["devices", "list", filters] as const,
  device: (id: string) => ["devices", "detail", id] as const,
  telemetry: (id: string, hours: number) => ["devices", "telemetry", id, hours] as const,
  telemetrySeries: (id: string, hours: number, bucket: TelemetryBucket) =>
    ["devices", "telemetry-series", id, hours, bucket] as const,
  webhookEvents: (filters: WebhookEventFilters) => ["webhook-events", "list", filters] as const,
  gateways: (filters: GatewayFilters) => ["gateways", "list", filters] as const,
  gatewayOptions: (organizationId?: string) =>
    ["gateways", "options", organizationId ?? "own"] as const,
  gatewayOutages: (open: boolean, page: number) => ["gateways", "outages", open, page] as const,
  installations: (deviceId: string) => ["devices", "installations", deviceId] as const,
  sites: (filters: SiteFilters) => ["sites", "list", filters] as const,
  site: (id: string) => ["sites", "detail", id] as const,
  siteOptions: (organizationId?: string) => ["sites", "options", organizationId ?? "own"] as const,
  assets: (filters: AssetFilters) => ["assets", "list", filters] as const,
  temperatureRecord: (assetId: string, from: string, to: string) =>
    ["assets", "temperature-record", assetId, from, to] as const,
  assetOptions: (organizationId?: string) =>
    ["assets", "options", organizationId ?? "own"] as const,
  deviceOptions: (siteId: string, organizationId?: string) =>
    ["devices", "options", siteId, organizationId ?? "own"] as const,
  ruleDeviceOptions: (organizationId?: string) =>
    ["devices", "options", "rule", organizationId ?? "own"] as const,
  users: (filters: UserFilters) => ["users", "list", filters] as const,
  organizations: (filters: OrganizationFilters) => ["organizations", "list", filters] as const,
  organizationOptions: ["organizations", "options"] as const,
  incidents: (filters: IncidentFilters) => ["incidents", "list", filters] as const,
  auditLogs: (filters: AuditLogFilters) => ["audit-logs", "list", filters] as const,
  alertRules: (filters: AlertRuleFilters) => ["alert-rules", "list", filters] as const,
  messagingContacts: (filters: MessagingContactFilters) =>
    ["messaging-contacts", "list", filters] as const,
  loanAgreements: (filters: LoanAgreementFilters) => ["loan-agreements", "list", filters] as const,
  loanAgreement: (id: string) => ["loan-agreements", "detail", id] as const,
  serviceAgreements: (filters: ServiceAgreementFilters) =>
    ["service-agreements", "list", filters] as const,
  serviceAgreement: (id: string) => ["service-agreements", "detail", id] as const,
  reconciliation: ["network", "reconciliation"] as const,
};

/** How many rows a list screen asks for. The API caps `limit` at 100. */
const PAGE_SIZE = 20;

/**
 * Enough of a picker to fill a `<select>` without paging through it. An
 * organization with more than this many sites needs a searchable control,
 * which is a problem worth having before it is worth solving.
 */
const OPTIONS_LIMIT = 100;

export interface DeviceFilters {
  search?: string;
  connectivity?: string;
  status?: string;
  siteId?: string;
  page?: number;
}

/**
 * Filters every list screen shares.
 *
 * `organizationId` only does anything for a super admin, who has no tenant of
 * their own: for everyone else the API ignores it and answers with their own
 * organization. It is part of the query key so switching tenants does not
 * read back a list belonging to the previous one.
 */
export interface ListFilters {
  search?: string;
  organizationId?: string;
  page?: number;
}

export type SiteFilters = ListFilters;

export interface GatewayFilters extends ListFilters {
  status?: string;
  ownership?: string;
  siteId?: string;
  /**
   * Shared platform infrastructure only. Does nothing for a tenant, who
   * never sees it in a listing at all — their devices report coverage by it
   * without being told what it is (ADR-062).
   */
  shared?: boolean;
}

export interface AssetFilters extends ListFilters {
  siteId?: string;
  type?: string;
}

export interface UserFilters extends ListFilters {
  status?: string;
  role?: string;
}

export interface OrganizationFilters {
  search?: string;
  status?: string;
  page?: number;
}

export interface IncidentFilters extends ListFilters {
  status?: string;
  type?: string;
  severity?: string;
  /**
   * Whether to hide what is already over. Defaults to true at the API, which
   * is the view an operator opening this screen wants: what still needs
   * attention. Naming a `status` overrides it.
   */
  unresolved?: boolean;
}

export interface AuditLogFilters extends ListFilters {
  action?: string;
  resourceType?: string;
  /** Everything one person did. Set from the actor cell on the screen. */
  actorUserId?: string;
  /** The history of one device, asset or member. */
  resourceId?: string;
  /**
   * How far back to look, as a width rather than a boundary.
   *
   * The concrete `from` is computed when the request goes out, the same way
   * `telemetryQuery` does it: reading the clock while rendering would give
   * the query key a new value on every pass and refetch forever.
   */
  hours?: number;
}

/** Page and free-text search, shaped the way `ApiClient` wants its query. */
function listQuery(filters: ListFilters | OrganizationFilters) {
  return {
    page: filters.page ?? 1,
    limit: PAGE_SIZE,
    ...(filters.search ? { search: filters.search } : {}),
  };
}

/**
 * Public hardware visibility flags.
 *
 * Stale for a long time: the map only changes when the API is redeployed with
 * a new FEATURE_FLAGS value.
 */
export function featuresQuery(client: ApiClient) {
  return queryOptions({
    queryKey: queryKeys.features,
    queryFn: (): Promise<FeaturesResponse> => client.getFeatures(),
    staleTime: 30 * 60_000,
  });
}

/** Delivery switches for the whole deployment (ADR-103). Super admin only. */
export function platformSettingsQuery(client: ApiClient) {
  return queryOptions({
    queryKey: ["platform-settings"] as const,
    queryFn: () =>
      client.get<PlatformSettings>("/platform-settings", { schema: platformSettingsSchema }),
  });
}

export function updatePlatformSettingsMutation(
  client: ApiClient,
): UseMutationOptions<PlatformSettings, Error, UpdatePlatformSettingsRequest> {
  return {
    mutationFn: (body) =>
      client.patch<PlatformSettings>("/platform-settings", {
        body,
        schema: platformSettingsSchema,
      }),
  };
}

/**
 * A comparison with The Things Stack (ADR-089). A POST, because the API
 * records each managed row's sync state while comparing; held until the
 * operator asks again, since every run reads every registry.
 */
export function reconciliationQuery(client: ApiClient) {
  return queryOptions({
    queryKey: queryKeys.reconciliation,
    queryFn: () =>
      client.post<ReconciliationReport>("/network/reconciliation", {
        schema: reconciliationReportSchema,
      }),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export type ReconciliationCommand =
  | { kind: "device"; action: "LINK"; id: string; networkId: string }
  | { kind: "device"; action: "PUSH"; id: string }
  | { kind: "gateway"; action: "LINK"; id: string; networkId: string }
  | { kind: "gateway"; action: "PUSH" | "REGISTER"; id: string }
  | { kind: "gateway"; action: "IMPORT"; networkId: string };

export function reconciliationActionMutation(
  client: ApiClient,
): UseMutationOptions<void, Error, ReconciliationCommand> {
  const base = "/network/reconciliation";

  return {
    mutationFn: async (command) => {
      if (command.action === "IMPORT") {
        await client.post(`${base}/gateways/import`, {
          body: { networkGatewayId: command.networkId },
        });
        return;
      }

      const path = `${base}/${command.kind}s/${command.id}/${command.action.toLowerCase()}`;
      const body =
        command.action === "LINK"
          ? command.kind === "device"
            ? { networkDeviceId: command.networkId }
            : { networkGatewayId: command.networkId }
          : undefined;

      await client.post(path, body ? { body } : {});
    },
  };
}

/**
 * Whether a hardware (or catalogue) id should be shown.
 *
 * Missing map (query still loading or failed) means enabled — same default as
 * the API when `FEATURE_FLAGS.enabledHardware` is absent. Marketing gates on
 * `features.isSuccess` separately so a disabled model cannot flash on
 * `/equipos`. Unknown ids (pending catalogue entries) stay visible.
 */
export function isHardwareEnabledInMap(map: EnabledHardwareMap | undefined, id: string): boolean {
  if (map === undefined) {
    return true;
  }

  if (Object.prototype.hasOwnProperty.call(map, id)) {
    return map[id as keyof EnabledHardwareMap];
  }

  return true;
}

export function dashboardSummaryQuery(client: ApiClient) {
  return queryOptions({
    queryKey: queryKeys.dashboard,
    queryFn: () =>
      client.get<DashboardSummary>("/dashboard/summary", { schema: dashboardSummarySchema }),
    // Same cadence as incidents: a dashboard left open has to notice a
    // device that goes quiet or an incident that opens, not the state it
    // happened to load with.
    refetchInterval: 60_000,
  });
}

export function devicesQuery(client: ApiClient, filters: DeviceFilters) {
  return queryOptions({
    queryKey: queryKeys.devices(filters),
    queryFn: () =>
      client.get<DeviceList>("/devices", {
        schema: deviceListSchema,
        query: {
          page: filters.page ?? 1,
          limit: 20,
          ...(filters.search ? { search: filters.search } : {}),
          ...(filters.connectivity ? { connectivity: filters.connectivity } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.siteId ? { siteId: filters.siteId } : {}),
        },
      }),
  });
}

export function deviceQuery(client: ApiClient, id: string) {
  return queryOptions({
    queryKey: queryKeys.device(id),
    queryFn: () => client.get<Device>(`/devices/${id}`, { schema: deviceSchema }),
  });
}

export function telemetryQuery(client: ApiClient, id: string, hours: number) {
  return queryOptions({
    queryKey: queryKeys.telemetry(id, hours),
    queryFn: () => {
      const to = new Date();
      const from = new Date(to.getTime() - hours * 60 * 60 * 1_000);

      return client.get<TelemetryList>(`/devices/${id}/telemetry`, {
        schema: telemetryListSchema,
        query: {
          from: from.toISOString(),
          to: to.toISOString(),
          // A chart is read as a shape, not row by row: one page of points is
          // enough to see an excursion and keeps the payload small.
          limit: 100,
        },
      });
    },
  });
}

/**
 * Hourly or daily aggregates for windows where a raw page of 100 would hide
 * the earlier part of the period (ADR-087).
 */
export function telemetrySeriesQuery(
  client: ApiClient,
  id: string,
  hours: number,
  bucket: TelemetryBucket,
) {
  return queryOptions({
    queryKey: queryKeys.telemetrySeries(id, hours, bucket),
    queryFn: () => {
      const to = new Date();
      const from = new Date(to.getTime() - hours * 60 * 60 * 1_000);

      return client.get<TelemetrySeries>(`/devices/${id}/telemetry/series`, {
        schema: telemetrySeriesSchema,
        query: {
          from: from.toISOString(),
          to: to.toISOString(),
          bucket,
        },
      });
    },
  });
}

/** Chart helpers treat an aggregate bucket as a single synthetic reading. */
export function seriesPointsAsReadings(series: TelemetrySeries): TelemetryReading[] {
  return series.data.map((point, index) => ({
    id: `00000000-0000-7000-8000-${String(index).padStart(12, "0")}`,
    measuredAt: point.bucketStart,
    receivedAt: point.bucketStart,
    temperatureC: point.temperatureC.avg,
    humidityPercent: point.humidityPercent.avg,
    batteryPercent: point.batteryPercent.avg,
    distanceMm: point.distanceMm.avg,
    currentA: point.currentA.avg,
    occupied: point.occupied,
    leaking: point.leaking,
    doorOpen: point.doorOpen,
    rssi: point.rssi.avg === null ? null : Math.round(point.rssi.avg),
    snr: point.snr.avg,
  }));
}

export interface WebhookEventFilters extends ListFilters {
  status?: WebhookEventStatus;
  search?: string;
}

export function webhookEventsQuery(client: ApiClient, filters: WebhookEventFilters) {
  return queryOptions({
    queryKey: queryKeys.webhookEvents(filters),
    queryFn: () =>
      client.get<WebhookEventList>("/integrations/lorawan/webhook-events", {
        schema: webhookEventListSchema,
        query: {
          page: filters.page ?? 1,
          limit: PAGE_SIZE,
          status: filters.status ?? "DEAD_LETTER",
          ...(filters.search ? { search: filters.search } : {}),
        },
      }),
  });
}

export function simulateUplinkMutation(
  client: ApiClient,
): UseMutationOptions<SimulateUplinkResponse, Error, SimulateUplinkRequest> {
  return {
    mutationFn: (body) =>
      client.post<SimulateUplinkResponse>("/integrations/lorawan/simulate", {
        schema: simulateUplinkResponseSchema,
        body,
      }),
  };
}

/**
 * Unpaginated readers for the `<select>` controls on the device forms, kept
 * separate from the list screens: a picker wants every option at once and
 * tolerates a stale one, a table wants a page and wants it current.
 */
export function siteOptionsQuery(client: ApiClient, organizationId?: string) {
  return queryOptions({
    queryKey: queryKeys.siteOptions(organizationId),
    queryFn: () =>
      client.get<SiteList>("/sites", {
        schema: siteListSchema,
        query: { limit: OPTIONS_LIMIT, ...(organizationId ? { organizationId } : {}) },
      }),
    // Sites and assets change far less often than readings do.
    staleTime: 5 * 60_000,
  });
}

/**
 * Devices at one site, enough of them to fill a picker.
 *
 * Scoped to a site because that is the only place a loan agreement can draw
 * equipment from: the contract names where the equipment is installed.
 */
export function deviceOptionsQuery(client: ApiClient, siteId: string, organizationId?: string) {
  return queryOptions({
    queryKey: queryKeys.deviceOptions(siteId, organizationId),
    queryFn: () =>
      client.get<DeviceList>("/devices", {
        schema: deviceListSchema,
        query: { limit: OPTIONS_LIMIT, siteId, ...(organizationId ? { organizationId } : {}) },
      }),
    staleTime: 5 * 60_000,
  });
}

export function assetOptionsQuery(client: ApiClient, organizationId?: string) {
  return queryOptions({
    queryKey: queryKeys.assetOptions(organizationId),
    queryFn: () =>
      client.get<AssetList>("/assets", {
        schema: assetListSchema,
        query: { limit: OPTIONS_LIMIT, ...(organizationId ? { organizationId } : {}) },
      }),
    staleTime: 5 * 60_000,
  });
}

/** Only active tenants: a suspended one is not a valid place to file anything. */
export function organizationOptionsQuery(client: ApiClient) {
  return queryOptions({
    queryKey: queryKeys.organizationOptions,
    queryFn: () =>
      client.get<OrganizationList>("/organizations", {
        schema: organizationListSchema,
        query: { limit: OPTIONS_LIMIT, status: "ACTIVE" },
      }),
    staleTime: 5 * 60_000,
  });
}

export function sitesQuery(client: ApiClient, filters: SiteFilters) {
  return queryOptions({
    queryKey: queryKeys.sites(filters),
    queryFn: () =>
      client.get<SiteList>("/sites", {
        schema: siteListSchema,
        query: {
          ...listQuery(filters),
          ...(filters.organizationId ? { organizationId: filters.organizationId } : {}),
        },
      }),
  });
}

export function siteQuery(client: ApiClient, id: string) {
  return queryOptions({
    queryKey: queryKeys.site(id),
    queryFn: () => client.get<Site>(`/sites/${id}`, { schema: siteSchema }),
  });
}

export function gatewaysQuery(client: ApiClient, filters: GatewayFilters) {
  return queryOptions({
    queryKey: queryKeys.gateways(filters),
    queryFn: () =>
      client.get<GatewayList>("/gateways", {
        schema: gatewayListSchema,
        query: {
          ...listQuery(filters),
          ...(filters.organizationId ? { organizationId: filters.organizationId } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.ownership ? { ownership: filters.ownership } : {}),
          ...(filters.siteId ? { siteId: filters.siteId } : {}),
          ...(filters.shared ? { shared: true } : {}),
        },
      }),
    // The API polls the Gateway Server once a minute; a list left open on a
    // technician's phone while mounting one should see it come online.
    refetchInterval: 60_000,
  });
}

/** Super admin only (ADR-093). Polled like the gateway list it sits above. */
export function gatewayOutagesQuery(client: ApiClient, open: boolean, page: number) {
  return queryOptions({
    queryKey: queryKeys.gatewayOutages(open, page),
    queryFn: () =>
      client.get<GatewayOutageList>("/gateway-outages", {
        schema: gatewayOutageListSchema,
        query: { open, page, limit: PAGE_SIZE },
      }),
    refetchInterval: 60_000,
  });
}

export function acknowledgeGatewayOutageMutation(
  client: ApiClient,
): UseMutationOptions<GatewayOutage, Error, { id: string; body: AcknowledgeGatewayOutageRequest }> {
  return {
    mutationFn: ({ id, body }) =>
      client.post<GatewayOutage>(`/gateway-outages/${id}/acknowledge`, {
        body,
        schema: gatewayOutageSchema,
      }),
  };
}

/**
 * Gateways a device can be pointed at, for the picker in the install flow.
 *
 * Deliberately the same endpoint as the listing, so a tenant is offered
 * exactly what they can see: their own hardware. Shared infrastructure is
 * attached by a super admin, who does see it here.
 */
export function gatewayOptionsQuery(client: ApiClient, organizationId?: string) {
  return queryOptions({
    queryKey: queryKeys.gatewayOptions(organizationId),
    queryFn: () =>
      client.get<GatewayList>("/gateways", {
        schema: gatewayListSchema,
        query: {
          limit: OPTIONS_LIMIT,
          ...(organizationId ? { organizationId } : {}),
        },
      }),
    staleTime: 60_000,
  });
}

export function installationsQuery(client: ApiClient, deviceId: string) {
  return queryOptions({
    queryKey: queryKeys.installations(deviceId),
    queryFn: () =>
      client.get<DeviceInstallationList>(`/devices/${deviceId}/installations`, {
        schema: deviceInstallationListSchema,
      }),
  });
}

/**
 * The temperature record for one asset over one period.
 *
 * The period is passed in explicitly rather than as a width in hours, unlike
 * `telemetryQuery`: a record is produced for a named period — March, or the
 * week an inspector asked about — and a window that slides with the clock is
 * the one thing a document meant to be filed must not do.
 */
export function temperatureRecordQuery(
  client: ApiClient,
  assetId: string,
  from: string,
  to: string,
) {
  return queryOptions({
    queryKey: queryKeys.temperatureRecord(assetId, from, to),
    queryFn: () =>
      client.get<TemperatureRecord>(`/assets/${assetId}/temperature-record`, {
        schema: temperatureRecordSchema,
        query: { from, to },
      }),
    // Every request is written to the audit trail, so a refetch on every
    // window focus would fill it with entries nobody asked for.
    refetchOnWindowFocus: false,
  });
}

export function assetsQuery(client: ApiClient, filters: AssetFilters) {
  return queryOptions({
    queryKey: queryKeys.assets(filters),
    queryFn: () =>
      client.get<AssetList>("/assets", {
        schema: assetListSchema,
        query: {
          ...listQuery(filters),
          ...(filters.organizationId ? { organizationId: filters.organizationId } : {}),
          ...(filters.siteId ? { siteId: filters.siteId } : {}),
          ...(filters.type ? { type: filters.type } : {}),
        },
      }),
  });
}

export function usersQuery(client: ApiClient, filters: UserFilters) {
  return queryOptions({
    queryKey: queryKeys.users(filters),
    queryFn: () =>
      client.get<UserList>("/users", {
        schema: userListSchema,
        query: {
          ...listQuery(filters),
          ...(filters.organizationId ? { organizationId: filters.organizationId } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.role ? { role: filters.role } : {}),
        },
      }),
  });
}

export function organizationsQuery(client: ApiClient, filters: OrganizationFilters) {
  return queryOptions({
    queryKey: queryKeys.organizations(filters),
    queryFn: () =>
      client.get<OrganizationList>("/organizations", {
        schema: organizationListSchema,
        query: {
          ...listQuery(filters),
          ...(filters.status ? { status: filters.status } : {}),
        },
      }),
  });
}

export function incidentsQuery(client: ApiClient, filters: IncidentFilters) {
  return queryOptions({
    queryKey: queryKeys.incidents(filters),
    queryFn: () =>
      client.get<IncidentList>("/incidents", {
        schema: incidentListSchema,
        query: {
          ...listQuery(filters),
          ...(filters.organizationId ? { organizationId: filters.organizationId } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.type ? { type: filters.type } : {}),
          ...(filters.severity ? { severity: filters.severity } : {}),
          ...(filters.unresolved === undefined ? {} : { unresolved: filters.unresolved }),
        },
      }),
    // An incident list that is minutes stale is a list that says a freezer is
    // fine when it is not.
    refetchInterval: 60_000,
  });
}

export function acknowledgeIncidentMutation(
  client: ApiClient,
): UseMutationOptions<Incident, Error, { id: string; body: AcknowledgeIncidentRequest }> {
  return {
    mutationFn: ({ id, body }) =>
      client.post<Incident>(`/incidents/${id}/acknowledge`, { body, schema: incidentSchema }),
  };
}

export interface AlertRuleFilters extends ListFilters {
  metric?: AlertMetric;
  enabled?: boolean;
  withoutRecipients?: boolean;
}

export function alertRulesQuery(client: ApiClient, filters: AlertRuleFilters) {
  return queryOptions({
    queryKey: queryKeys.alertRules(filters),
    queryFn: () =>
      client.get<AlertRuleList>("/alert-rules", {
        schema: alertRuleListSchema,
        query: {
          ...listQuery(filters),
          ...(filters.organizationId ? { organizationId: filters.organizationId } : {}),
          ...(filters.metric ? { metric: filters.metric } : {}),
          ...(filters.enabled === undefined ? {} : { enabled: filters.enabled }),
          ...(filters.withoutRecipients === undefined
            ? {}
            : { withoutRecipients: filters.withoutRecipients }),
        },
      }),
  });
}

/** Every device in one organization, for scoping a new rule to one of them. */
export function ruleDeviceOptionsQuery(client: ApiClient, organizationId?: string) {
  return queryOptions({
    queryKey: queryKeys.ruleDeviceOptions(organizationId),
    queryFn: () =>
      client.get<DeviceList>("/devices", {
        schema: deviceListSchema,
        query: { limit: OPTIONS_LIMIT, ...(organizationId ? { organizationId } : {}) },
      }),
    staleTime: 5 * 60_000,
  });
}

export function createAlertRuleMutation(
  client: ApiClient,
): UseMutationOptions<AlertRule, Error, CreateAlertRuleRequest> {
  return {
    mutationFn: (body) => client.post<AlertRule>("/alert-rules", { body, schema: alertRuleSchema }),
  };
}

export function createDefaultAlertRulesMutation(
  client: ApiClient,
): UseMutationOptions<CreateDefaultAlertRulesResponse, Error, CreateDefaultAlertRulesRequest> {
  return {
    mutationFn: (body) =>
      client.post<CreateDefaultAlertRulesResponse>("/alert-rules/defaults", {
        body,
        schema: createDefaultAlertRulesResponseSchema,
      }),
  };
}

export function updateAlertRuleMutation(
  client: ApiClient,
  id: string,
): UseMutationOptions<AlertRule, Error, UpdateAlertRuleRequest> {
  return {
    mutationFn: (body) =>
      client.patch<AlertRule>(`/alert-rules/${id}`, { body, schema: alertRuleSchema }),
  };
}

/** Super-admin only: prove the WhatsApp Cloud API can accept the incident template. */
export function testIncidentWhatsAppMutation(
  client: ApiClient,
): UseMutationOptions<TestIncidentWhatsAppResponse, Error, TestIncidentWhatsAppRequest> {
  return {
    mutationFn: (body) =>
      client.post<TestIncidentWhatsAppResponse>("/notifications/test-whatsapp", {
        body,
        schema: testIncidentWhatsAppResponseSchema,
      }),
  };
}

/** Super-admin only: prove Resend can deliver the incident template. */
export function testIncidentEmailMutation(
  client: ApiClient,
): UseMutationOptions<TestIncidentEmailResponse, Error, TestIncidentEmailRequest> {
  return {
    mutationFn: (body) =>
      client.post<TestIncidentEmailResponse>("/notifications/test-email", {
        body,
        schema: testIncidentEmailResponseSchema,
      }),
  };
}

export interface MessagingContactFilters extends ListFilters {
  includeRevoked?: boolean;
  /** Overrides the shared page size. The rule dialog asks for the whole list. */
  limit?: number;
}

export function messagingContactsQuery(client: ApiClient, filters: MessagingContactFilters) {
  return queryOptions({
    queryKey: queryKeys.messagingContacts(filters),
    queryFn: () =>
      client.get<MessagingContactList>("/messaging-contacts", {
        schema: messagingContactListSchema,
        query: {
          ...listQuery(filters),
          ...(filters.limit === undefined ? {} : { limit: filters.limit }),
          ...(filters.organizationId ? { organizationId: filters.organizationId } : {}),
          ...(filters.includeRevoked === undefined
            ? {}
            : { includeRevoked: filters.includeRevoked }),
        },
      }),
  });
}

export function createMessagingContactMutation(
  client: ApiClient,
): UseMutationOptions<MessagingContact, Error, CreateMessagingContactRequest> {
  return {
    mutationFn: (body) =>
      client.post<MessagingContact>("/messaging-contacts", {
        body,
        schema: messagingContactSchema,
      }),
  };
}

/**
 * Withdrawing and recording consent are separate calls, as the API models
 * them: neither is a flag flip, and each leaves its own audit entry.
 */
export function revokeConsentMutation(
  client: ApiClient,
): UseMutationOptions<MessagingContact, Error, { id: string; body: RevokeConsentRequest }> {
  return {
    mutationFn: ({ id, body }) =>
      client.post<MessagingContact>(`/messaging-contacts/${id}/revoke-consent`, {
        body,
        schema: messagingContactSchema,
      }),
  };
}

export function reinstateConsentMutation(
  client: ApiClient,
): UseMutationOptions<MessagingContact, Error, { id: string; body: ReinstateConsentRequest }> {
  return {
    mutationFn: ({ id, body }) =>
      client.post<MessagingContact>(`/messaging-contacts/${id}/reinstate-consent`, {
        body,
        schema: messagingContactSchema,
      }),
  };
}

export function auditLogsQuery(client: ApiClient, filters: AuditLogFilters) {
  return queryOptions({
    queryKey: queryKeys.auditLogs(filters),
    queryFn: () => {
      const from =
        filters.hours === undefined
          ? undefined
          : new Date(Date.now() - filters.hours * 60 * 60 * 1_000).toISOString();

      return client.get<AuditLogList>("/audit-logs", {
        schema: auditLogListSchema,
        query: {
          ...listQuery(filters),
          ...(filters.organizationId ? { organizationId: filters.organizationId } : {}),
          ...(filters.action ? { action: filters.action } : {}),
          ...(filters.resourceType ? { resourceType: filters.resourceType } : {}),
          ...(filters.actorUserId ? { actorUserId: filters.actorUserId } : {}),
          ...(filters.resourceId ? { resourceId: filters.resourceId } : {}),
          ...(from ? { from } : {}),
        },
      });
    },
  });
}

export function createSiteMutation(
  client: ApiClient,
): UseMutationOptions<Site, Error, CreateSiteRequest> {
  return {
    mutationFn: (body) => client.post<Site>("/sites", { body, schema: siteSchema }),
  };
}

export function updateSiteMutation(
  client: ApiClient,
): UseMutationOptions<Site, Error, { id: string; body: UpdateSiteRequest }> {
  return {
    mutationFn: ({ id, body }) => client.patch<Site>(`/sites/${id}`, { body, schema: siteSchema }),
  };
}

export function createAssetMutation(
  client: ApiClient,
): UseMutationOptions<Asset, Error, CreateAssetRequest> {
  return {
    mutationFn: (body) => client.post<Asset>("/assets", { body, schema: assetSchema }),
  };
}

export function updateAssetMutation(
  client: ApiClient,
): UseMutationOptions<Asset, Error, { id: string; body: UpdateAssetRequest }> {
  return {
    mutationFn: ({ id, body }) =>
      client.patch<Asset>(`/assets/${id}`, { body, schema: assetSchema }),
  };
}

export function deleteAssetMutation(
  client: ApiClient,
): UseMutationOptions<void, Error, { id: string }> {
  return {
    mutationFn: ({ id }) => client.delete(`/assets/${id}`),
  };
}

export function createGatewayMutation(
  client: ApiClient,
): UseMutationOptions<Gateway, Error, CreateGatewayRequest> {
  return {
    mutationFn: (body) => client.post<Gateway>("/gateways", { body, schema: gatewaySchema }),
  };
}

export function updateGatewayMutation(
  client: ApiClient,
): UseMutationOptions<Gateway, Error, { id: string; body: UpdateGatewayRequest }> {
  return {
    mutationFn: ({ id, body }) =>
      client.patch<Gateway>(`/gateways/${id}`, { body, schema: gatewaySchema }),
  };
}

export function deleteGatewayMutation(
  client: ApiClient,
): UseMutationOptions<void, Error, { id: string }> {
  return {
    mutationFn: ({ id }) => client.delete(`/gateways/${id}`),
  };
}

/**
 * The installation procedure, one mutation per step.
 *
 * There is no mutation for commissioning a device, and there is no endpoint
 * to call either: a device becomes operational when a reading arrives through
 * one of its configured gateways, which is something only the sensor can do
 * (ADR-062).
 */
export function openInstallationMutation(
  client: ApiClient,
): UseMutationOptions<
  DeviceInstallation,
  Error,
  { deviceId: string; body: OpenInstallationRequest }
> {
  return {
    mutationFn: ({ deviceId, body }) =>
      client.post<DeviceInstallation>(`/devices/${deviceId}/installations`, {
        body,
        schema: deviceInstallationSchema,
      }),
  };
}

export function linkGatewaysMutation(
  client: ApiClient,
): UseMutationOptions<DeviceInstallation, Error, { deviceId: string; body: LinkGatewaysRequest }> {
  return {
    mutationFn: ({ deviceId, body }) =>
      client.post<DeviceInstallation>(`/devices/${deviceId}/gateways`, {
        body,
        schema: deviceInstallationSchema,
      }),
  };
}

export function unlinkGatewayMutation(
  client: ApiClient,
): UseMutationOptions<void, Error, { deviceId: string; linkId: string }> {
  return {
    mutationFn: ({ deviceId, linkId }) => client.delete(`/devices/${deviceId}/gateways/${linkId}`),
  };
}

export function failInstallationMutation(
  client: ApiClient,
): UseMutationOptions<
  DeviceInstallation,
  Error,
  { deviceId: string; body: FailInstallationRequest }
> {
  return {
    mutationFn: ({ deviceId, body }) =>
      client.post<DeviceInstallation>(`/devices/${deviceId}/installations/fail`, {
        body,
        schema: deviceInstallationSchema,
      }),
  };
}

export function createUserMutation(
  client: ApiClient,
): UseMutationOptions<User, Error, CreateUserRequest> {
  return {
    mutationFn: (body) => client.post<User>("/users", { body, schema: userSchema }),
  };
}

export function updateUserMutation(
  client: ApiClient,
): UseMutationOptions<User, Error, { id: string; body: UpdateUserRequest }> {
  return {
    mutationFn: ({ id, body }) => client.patch<User>(`/users/${id}`, { body, schema: userSchema }),
  };
}

export function createOrganizationMutation(
  client: ApiClient,
): UseMutationOptions<Organization, Error, CreateOrganizationRequest> {
  return {
    mutationFn: (body) =>
      client.post<Organization>("/organizations", { body, schema: organizationSchema }),
  };
}

/**
 * Suspend and reactivate are separate calls rather than a status field, the
 * same way the API models them: each one is a decision with consequences and
 * its own audit entry.
 */
export function suspendOrganizationMutation(
  client: ApiClient,
): UseMutationOptions<Organization, Error, { id: string; body: SuspendOrganizationRequest }> {
  return {
    mutationFn: ({ id, body }) =>
      client.post<Organization>(`/organizations/${id}/suspend`, {
        body,
        schema: organizationSchema,
      }),
  };
}

export function reactivateOrganizationMutation(
  client: ApiClient,
): UseMutationOptions<Organization, Error, string> {
  return {
    mutationFn: (id) =>
      client.post<Organization>(`/organizations/${id}/reactivate`, { schema: organizationSchema }),
  };
}

export function createDeviceMutation(
  client: ApiClient,
): UseMutationOptions<Device, Error, CreateDeviceRequest> {
  return {
    mutationFn: (body) => client.post<Device>("/devices", { body, schema: deviceSchema }),
  };
}

export function updateDeviceMutation(
  client: ApiClient,
  id: string,
): UseMutationOptions<Device, Error, UpdateDeviceRequest> {
  return {
    mutationFn: (body) => client.patch<Device>(`/devices/${id}`, { body, schema: deviceSchema }),
  };
}

export function deactivateDeviceMutation(
  client: ApiClient,
  id: string,
): UseMutationOptions<Device, Error, void> {
  return {
    mutationFn: () => client.post<Device>(`/devices/${id}/deactivate`, { schema: deviceSchema }),
  };
}

export function updateDeviceChannelMutation(
  client: ApiClient,
  deviceId: string,
  channelId: string,
): UseMutationOptions<DeviceChannel, Error, UpdateDeviceChannelRequest> {
  return {
    mutationFn: (body) =>
      client.patch<DeviceChannel>(`/devices/${deviceId}/channels/${channelId}`, {
        body,
        schema: deviceChannelSchema,
      }),
  };
}

export function controlDeviceChannelMutation(
  client: ApiClient,
  deviceId: string,
  channelId: string,
): UseMutationOptions<ControlDeviceChannelResponse, Error, ControlDeviceChannelRequest> {
  return {
    mutationFn: (body) =>
      client.post<ControlDeviceChannelResponse>(
        `/devices/${deviceId}/channels/${channelId}/control`,
        {
          body,
          schema: controlDeviceChannelResponseSchema,
        },
      ),
  };
}

export interface LoanAgreementFilters extends ListFilters {
  status?: LoanAgreementStatus;
  siteId?: string;
  endingWithinDays?: number;
}

export function loanAgreementsQuery(client: ApiClient, filters: LoanAgreementFilters) {
  return queryOptions({
    queryKey: queryKeys.loanAgreements(filters),
    queryFn: () =>
      client.get<LoanAgreementList>("/loan-agreements", {
        schema: loanAgreementListSchema,
        query: {
          ...listQuery(filters),
          ...(filters.organizationId ? { organizationId: filters.organizationId } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.siteId ? { siteId: filters.siteId } : {}),
          ...(filters.endingWithinDays !== undefined
            ? { endingWithinDays: filters.endingWithinDays }
            : {}),
        },
      }),
  });
}

export function loanAgreementQuery(client: ApiClient, id: string) {
  return queryOptions({
    queryKey: queryKeys.loanAgreement(id),
    queryFn: () =>
      client.get<LoanAgreement>(`/loan-agreements/${id}`, { schema: loanAgreementSchema }),
  });
}

export function createLoanAgreementMutation(
  client: ApiClient,
): UseMutationOptions<LoanAgreement, Error, CreateLoanAgreementRequest> {
  return {
    mutationFn: (body) =>
      client.post<LoanAgreement>("/loan-agreements", { body, schema: loanAgreementSchema }),
  };
}

export function updateLoanAgreementMutation(
  client: ApiClient,
): UseMutationOptions<LoanAgreement, Error, { id: string; body: UpdateLoanAgreementRequest }> {
  return {
    mutationFn: ({ id, body }) =>
      client.patch<LoanAgreement>(`/loan-agreements/${id}`, {
        body,
        schema: loanAgreementSchema,
      }),
  };
}

/**
 * Issuing, signing and closing, one mutation per step.
 *
 * Deliberately not one `setStatus` call taking a target: the server decides
 * which transitions exist, and a generic mutation would invite a screen to
 * ask for one that does not.
 */
export function issueLoanAgreementMutation(
  client: ApiClient,
): UseMutationOptions<LoanAgreement, Error, { id: string }> {
  return {
    mutationFn: ({ id }) =>
      client.post<LoanAgreement>(`/loan-agreements/${id}/issue`, { schema: loanAgreementSchema }),
  };
}

export function attestSignatureMutation(
  client: ApiClient,
): UseMutationOptions<LoanAgreement, Error, { id: string; body: AttestSignatureRequest }> {
  return {
    mutationFn: ({ id, body }) =>
      client.post<LoanAgreement>(`/loan-agreements/${id}/attest-signature`, {
        body,
        schema: loanAgreementSchema,
      }),
  };
}

export function recordReturnMutation(
  client: ApiClient,
): UseMutationOptions<LoanAgreement, Error, { id: string; body: RecordReturnRequest }> {
  return {
    mutationFn: ({ id, body }) =>
      client.post<LoanAgreement>(`/loan-agreements/${id}/returns`, {
        body,
        schema: loanAgreementSchema,
      }),
  };
}

export function closeLoanAgreementMutation(
  client: ApiClient,
): UseMutationOptions<LoanAgreement, Error, { id: string }> {
  return {
    mutationFn: ({ id }) =>
      client.post<LoanAgreement>(`/loan-agreements/${id}/close`, { schema: loanAgreementSchema }),
  };
}

export function renewLoanAgreementMutation(
  client: ApiClient,
): UseMutationOptions<LoanAgreement, Error, { id: string }> {
  return {
    mutationFn: ({ id }) =>
      client.post<LoanAgreement>(`/loan-agreements/${id}/renew`, { schema: loanAgreementSchema }),
  };
}

export function cancelLoanAgreementMutation(
  client: ApiClient,
): UseMutationOptions<LoanAgreement, Error, { id: string }> {
  return {
    mutationFn: ({ id }) =>
      client.post<LoanAgreement>(`/loan-agreements/${id}/cancel`, { schema: loanAgreementSchema }),
  };
}

export interface ServiceAgreementFilters {
  page: number;
  search?: string;
  organizationId?: string;
  status?: ServiceAgreementStatus;
  siteId?: string;
  loanAgreementId?: string;
  endingWithinDays?: number;
}

export function serviceAgreementsQuery(client: ApiClient, filters: ServiceAgreementFilters) {
  return queryOptions({
    queryKey: queryKeys.serviceAgreements(filters),
    queryFn: () =>
      client.get<ServiceAgreementList>("/service-agreements", {
        schema: serviceAgreementListSchema,
        query: {
          ...listQuery(filters),
          ...(filters.organizationId ? { organizationId: filters.organizationId } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.siteId ? { siteId: filters.siteId } : {}),
          ...(filters.loanAgreementId ? { loanAgreementId: filters.loanAgreementId } : {}),
          ...(filters.endingWithinDays !== undefined
            ? { endingWithinDays: filters.endingWithinDays }
            : {}),
        },
      }),
  });
}

export function serviceAgreementQuery(client: ApiClient, id: string) {
  return queryOptions({
    queryKey: queryKeys.serviceAgreement(id),
    queryFn: () =>
      client.get<ServiceAgreement>(`/service-agreements/${id}`, {
        schema: serviceAgreementSchema,
      }),
  });
}

export function createServiceAgreementMutation(
  client: ApiClient,
): UseMutationOptions<ServiceAgreement, Error, CreateServiceAgreementRequest> {
  return {
    mutationFn: (body) =>
      client.post<ServiceAgreement>("/service-agreements", {
        body,
        schema: serviceAgreementSchema,
      }),
  };
}

export function updateServiceAgreementMutation(
  client: ApiClient,
): UseMutationOptions<
  ServiceAgreement,
  Error,
  { id: string; body: UpdateServiceAgreementRequest }
> {
  return {
    mutationFn: ({ id, body }) =>
      client.patch<ServiceAgreement>(`/service-agreements/${id}`, {
        body,
        schema: serviceAgreementSchema,
      }),
  };
}

export function issueServiceAgreementMutation(
  client: ApiClient,
): UseMutationOptions<ServiceAgreement, Error, { id: string }> {
  return {
    mutationFn: ({ id }) =>
      client.post<ServiceAgreement>(`/service-agreements/${id}/issue`, {
        schema: serviceAgreementSchema,
      }),
  };
}

export function attestServiceSignatureMutation(
  client: ApiClient,
): UseMutationOptions<
  ServiceAgreement,
  Error,
  { id: string; body: AttestServiceSignatureRequest }
> {
  return {
    mutationFn: ({ id, body }) =>
      client.post<ServiceAgreement>(`/service-agreements/${id}/attest-signature`, {
        body,
        schema: serviceAgreementSchema,
      }),
  };
}

export function closeServiceAgreementMutation(
  client: ApiClient,
): UseMutationOptions<ServiceAgreement, Error, { id: string }> {
  return {
    mutationFn: ({ id }) =>
      client.post<ServiceAgreement>(`/service-agreements/${id}/close`, {
        schema: serviceAgreementSchema,
      }),
  };
}

export function cancelServiceAgreementMutation(
  client: ApiClient,
): UseMutationOptions<ServiceAgreement, Error, { id: string }> {
  return {
    mutationFn: ({ id }) =>
      client.post<ServiceAgreement>(`/service-agreements/${id}/cancel`, {
        schema: serviceAgreementSchema,
      }),
  };
}

export function renewServiceAgreementMutation(
  client: ApiClient,
): UseMutationOptions<ServiceAgreement, Error, { id: string }> {
  return {
    mutationFn: ({ id }) =>
      client.post<ServiceAgreement>(`/service-agreements/${id}/renew`, {
        schema: serviceAgreementSchema,
      }),
  };
}
